/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, comparer, makeObservable, observable, reaction, toJS } from 'mobx';

import type { SelectionProps } from '@veeam/components';

import { SelectionMode } from '../../../enums';

import type { GridProps } from '../../../interfaces';
import type { GridStore } from '../grid-store';

export const generatedIdField = '__generatedId';

export class SelectionStore<RowDataT, TotalRowDataT, FilterT> {
    @observable.shallow selected: RowDataT[] = [];
    @observable selection: Partial<SelectionProps<RowDataT>>;
    @observable selectionMode: SelectionMode = SelectionMode.CurrentPage;

    withSelection: boolean;
    needToBeSelected: RowDataT[];
    disableAllPageSelection: boolean;
    isRowDisabled?: (rowData: RowDataT) => boolean;
    generatedIdField = generatedIdField;

    constructor(
        private gridStore: GridStore<RowDataT, TotalRowDataT, FilterT>,
        props: GridProps<RowDataT, TotalRowDataT, FilterT>
    ) {
        makeObservable(this);

        const { selection, disableAllPageSelection, onSelectionChange, initialState } = props;

        this.needToBeSelected = initialState?.selected;
        this.withSelection = !!selection;
        this.selection = {
            field: selection?.field ?? this.generatedIdField,
            multiple: selection?.multiple ?? true,
            checkbox: selection?.checkbox ?? true,
            fullLength: 0,
            isIndeterminate: false,
            isSelectedAll: false,
        };
        this.disableAllPageSelection = disableAllPageSelection;
        this.isRowDisabled = props.isRowDisabled;

        if (onSelectionChange) {
            reaction(
                () => this.selected,
                () => onSelectionChange(toJS(this.selected)),
                { equals: comparer.structural }
            );
        }
    }

    @action.bound
    setSelected(selected: RowDataT[]): void {
        this.selected = selected;
    }

    @action.bound
    selectAll(): void {
        this.selected = this.gridStore.pageData;
        this.selection.isIndeterminate = false;
        this.selectionMode = SelectionMode.CurrentPage;
        this.selection.isSelectedAll = this.gridStore.pageData && this.gridStore.pageData.length > 0;
    }

    @action.bound
    selectAllOnAllPages(): void {
        this.selectAll();

        if (this.gridStore.paginationStore.withPagination && this.gridStore.pagination.fullPages > 1) {
            this.selectionMode = SelectionMode.AllPages;
        }
    }

    @action.bound
    deselectAll(): void {
        this.selected = [];
        this.selection.isIndeterminate = false;
        this.selection.isSelectedAll = false;
        this.selectionMode = SelectionMode.CurrentPage;
    }

    @action.bound
    onSelectionChange(selected: RowDataT[]): void {
        this.selected = selected;
        this.selection.isIndeterminate = selected.length > 0 && selected.length < this.gridStore.pageData.length;
        this.selection.isSelectedAll = selected.length === this.gridStore.pageData.length;
    }

    checkIfAnyRowsNeedToBeSelected(): void {
        if (this.needToBeSelected && this.withSelection) {
            const existRows: RowDataT[] = this.needToBeSelected
                .map(rowToSelect => this.gridStore.pageData.find(gridRow => rowToSelect[this.selection.field] === gridRow[this.selection.field]))
                .filter(Boolean);

            if (existRows.length > 0) {
                this.selected = existRows;
                this.selection.isIndeterminate = existRows.length > 0 && existRows.length < this.gridStore.pageData.length;
                this.selection.isSelectedAll = existRows.length === this.gridStore.pageData.length;
            }

            this.needToBeSelected = null; // we wanna do it only once
        }
    }

    @action.bound
    onHeaderSelectionChange(): void {
        if (this.gridStore.loading) return;

        let checked: boolean;
        const initialSelectionState = this.selected.length;

        if (this.selection.isSelectedAll) {
            this.deselectAll();

            checked = false;
        } else {
            this.selectAll();

            checked = true;
        }

        const actualSelectionState = this.selected.length;

        this.onHeaderSelectionChangeWithExclusions(checked, initialSelectionState, actualSelectionState);
    }

    @action.bound
    private onHeaderSelectionChangeWithExclusions(checked: boolean, initialSelectionState: number, actualSelectionState: number) {
        if (!this.isRowDisabled) {
            return;
        }

        if (!checked) {
            return;
        }

        if (initialSelectionState === actualSelectionState) {
            this.deselectAll();

            return;
        }

        const actualSelection = this.selected.filter(row => !this.isRowDisabled(row));

        this.setSelected(actualSelection);

        this.selection.isIndeterminate = actualSelection.length > 0 && actualSelection.length < this.gridStore.pageData.length;
        this.selection.isSelectedAll = actualSelection.length === this.gridStore.pageData.length;
    }
}
